import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material';
import { UserinfoService } from '../components/index/service/userinfo.service';
import { environment } from '../../environments/environment';
import { LOGIN_TOKEN_KEY } from '../constants/auth.constants';
import { AuthService } from './auth.service';
import { BehaviorSubject, Subscription, timer } from 'rxjs';
import { SessionExpiryDialogComponent } from './session-expiry-dialog.component';

@Injectable({ providedIn: 'root' })
export class CiamService {
  private _loginSuccess = true;
  private loginCompletedSubject = new BehaviorSubject<boolean>(false);
  public loginCompleted$ = this.loginCompletedSubject.asObservable();
  private tokenEnsured = false;
  private ensuring: Promise<void> | null = null;
  private isRefresh = false;
  private expiryCheckSub: Subscription | null = null;
  private promptOpen = false;

  constructor(
    private authService: AuthService,
    private userinfoService: UserinfoService,
    private dialog: MatDialog
  ) {}

  /**
   * Ensures a CIAM token is present (if CIAM enabled) before proceeding.
   * Safe to call multiple times; returns the same Promise while in-flight.
   */
  ensureToken(): Promise<void> {
    if (this.tokenEnsured) {
      return Promise.resolve();
    }
    if (this.ensuring) {
      return this.ensuring;
    }

    this.ensuring = new Promise<void>((resolve) => {
      // Prefer preauth-provided reload flag; fall back to runtime detection
      const storedReload = sessionStorage.getItem('IS_PAGE_RELOAD');
      if (storedReload !== null) {
        this.isRefresh = storedReload === 'true';
        try { sessionStorage.removeItem('IS_PAGE_RELOAD'); } catch {}
      } else {
        const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
        if (navEntries && navEntries.length > 0) {
          this.isRefresh = navEntries[0].type === 'reload';
        } else if ((performance as any).navigation) { // legacy fallback
          this.isRefresh = (performance as any).navigation.type === 1;
        }
      }

      // On hard reload, force a fresh CIAM login and drop stale session data
      if (this.isRefresh) {
        this.redirectToCiamLogin();
        this.loginCompletedSubject.next(true);
        resolve();
        return;
      }

      if (!environment.useCiam) {
        this.tokenEnsured = true;
        this.loginCompletedSubject.next(true);
        resolve();
        return;
      }

      // Pre-auth gate may have already performed code exchange
      if (sessionStorage.getItem('CIAM_PREAUTH_DONE')) {
        const existingTokenEarly = sessionStorage.getItem(LOGIN_TOKEN_KEY);
        this.tokenEnsured = !!existingTokenEarly;
        if (existingTokenEarly) {
          this.scheduleExpiryPrompt(existingTokenEarly);
        }
        if (existingTokenEarly && !this.userinfoService.getUserinfo()) {
          console.time('[AuthFlow] userinfo_fetch');
          Promise.resolve(this.userinfoService.loadUserInfo()).then(() => {
            console.timeEnd('[AuthFlow] userinfo_fetch');
            this.loginCompletedSubject.next(true);
            resolve();
          }).catch(() => {
            this.loginCompletedSubject.next(true);
            resolve();
          });
        } else {
          this.loginCompletedSubject.next(true);
          resolve();
        }
        return;
      }

      const onCallback = window.location.href.includes('/auth/cim');
      const existingToken = sessionStorage.getItem(LOGIN_TOKEN_KEY);

      // Case: not callback & no token -> redirect (Angular bootstrap stops after navigation)
      if (!onCallback && !existingToken) {
        this.redirectToCiamLogin();
        // Do not resolve; navigation away. Mark completed to unblock any waiting logic if Angular continues.
        this.loginCompletedSubject.next(true);
        resolve();
        return;
      }

      // Case: token already present -> fetch user info if not cached
      if (existingToken) {
        this.tokenEnsured = true;
        this.scheduleExpiryPrompt(existingToken);
        if (!this.userinfoService.getUserinfo()) {
          console.time('[AuthFlow] userinfo_fetch');
          Promise.resolve(this.userinfoService.loadUserInfo()).then(() => {
            console.timeEnd('[AuthFlow] userinfo_fetch');
            this.loginCompletedSubject.next(true);
            resolve();
          }).catch(err => {
            console.warn('[CiamService] userinfo fetch failed on existing token', err);
            this.loginCompletedSubject.next(true);
            resolve();
          });
        } else {
          this.loginCompletedSubject.next(true);
          resolve();
        }
        return;
      }

      // Callback route: attempt code exchange
      const url = new URL(window.location.href);
      const code = url.searchParams.get('code');
      console.log('[CiamService] ensureToken code=', code);
      if (!code) {
        console.log('[CiamService] Callback without code -> marking unsuccessful.');
        this._loginSuccess = false;
        this.loginCompletedSubject.next(true);
        resolve();
        return;
      }

      console.log('[CiamService] Performing code exchange...');
      console.time('[AuthFlow] code_exchange');
      this.authService.sendAuthCode(code).toPromise().then(res => {
        const token = res && res.body && (res.body as any).token;
        console.log('[CiamService] Code exchange response received. Token present:', !!token);
        console.timeEnd('[AuthFlow] code_exchange');
        if (!token) {
          this._loginSuccess = false;
        } else {
          sessionStorage.setItem(LOGIN_TOKEN_KEY, token);
          this._loginSuccess = true;
          this.scheduleExpiryPrompt(token);
        }
        this.tokenEnsured = true;
        // Clean URL: drop code parameter
        try {
          const cleanUrl = window.location.origin + window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        } catch {}
        // Prefetch user info immediately (direct injection, no circular now)
        console.time('[AuthFlow] userinfo_fetch');
        Promise.resolve(this.userinfoService.loadUserInfo()).then(() => {
          console.timeEnd('[AuthFlow] userinfo_fetch');
          this.loginCompletedSubject.next(true);
          resolve();
        }).catch(err => {
          console.warn('[CiamService] userinfo prefetch failed', err);
          this.loginCompletedSubject.next(true);
          resolve();
        });
      }).catch(err => {
        console.log('[CiamService] Code exchange failed:', err);
        this._loginSuccess = false;
        this.loginCompletedSubject.next(true);
        resolve();
      });
    });

    return this.ensuring;
  }

  private scheduleExpiryPrompt(token: string): void {
    if (this.expiryCheckSub) {
      this.expiryCheckSub.unsubscribe();
      this.expiryCheckSub = null;
    }
    const expSec = this.getTokenExp(token);
    if (!expSec) {
      return;
    }
    const expTime = Math.max(expSec * 1000 - Date.now(), 0);
    console.log('[CiamService] scheduling token expiry prompt in ms:', expTime);
    const leadMs = 5000; // prompt 5 seconds before expiry
    const due = Math.max(expSec * 1000 - Date.now() - leadMs, 0);
    this.expiryCheckSub = timer(due).subscribe(() => this.showReauthPrompt());
  }

  private showReauthPrompt(): void {
    if (this.promptOpen) {
      return;
    }
    this.promptOpen = true;
    const ref = this.dialog.open(SessionExpiryDialogComponent, { disableClose: true });
    ref.afterClosed().subscribe(result => {
      this.promptOpen = false;
      if (result === 'renew') {
        sessionStorage.clear();
        this.redirectToCiamLogin();
      }
    });
  }

  private getTokenExp(token: string): number | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      const payload = JSON.parse(atob(parts[1]));
      if (payload && typeof payload.exp === 'number') {
        return payload.exp;
      }
    } catch {
      return null;
    }
    return null;
  }

  public redirectToCiamLogin(): void {
    if (!environment.ciamUrl) {
      console.warn('CIAM URL not configured');
      return;
    }
    window.location.replace(environment.ciamUrl);
  }

  get loginSuccess(): boolean {
    return this._loginSuccess;
  }

  set loginSuccess(success: boolean) {
    this._loginSuccess = success;
  }
}

